import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
  restrictToWindowEdges
} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import type React from 'react'
import { useCallback, useState } from 'react'
import type { ReorderableItemProps } from './ReorderableItem'

type ReorderableListProps<RenderedItemProps extends ReorderableItemProps> = {
  items: RenderedItemProps[]
  onChange: (props: RenderedItemProps[]) => void
  children: React.ReactNode
  /**
   * @default false
   */
  handle?: boolean
}

export const getItemById = <RenderedItemProps extends ReorderableItemProps>(
  id: string | number | undefined,
  items: RenderedItemProps[]
): RenderedItemProps =>
  items.find((item) => item.id === id) as RenderedItemProps

export const getReorderedArray = <
  RenderedItemProps extends ReorderableItemProps
>(
  items: RenderedItemProps[],
  activeId: string | number,
  overId: string | number | undefined
): RenderedItemProps[] => {
  const activeItem = getItemById(activeId, items)
  const oldIndex = items.indexOf(activeItem)

  const newIndex = items.indexOf(getItemById(overId, items))

  const newArray = arrayMove(items, oldIndex, newIndex)

  return newArray
}

const measuringConfig = {
  droppable: {
    strategy: MeasuringStrategy.Always
  }
}

function ReorderableList<
  RenderedItemProps extends ReorderableItemProps = ReorderableItemProps
>(props: ReorderableListProps<RenderedItemProps>) {
  const { items, onChange, children } = props

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (active.id !== over?.id) {
        const reorderedArray = getReorderedArray(items, active.id, over?.id)

        onChange(reorderedArray)
      }
    },
    [onChange, items]
  )

  return (
    <DndContext
      measuring={measuringConfig}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[
        restrictToVerticalAxis,
        restrictToFirstScrollableAncestor,
        restrictToWindowEdges
      ]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

export default ReorderableList
